/** Расчёты вкладки «План» (чистые функции): план на дату, отставание, прогноз на конец месяца
 *  выбранного режима (формулы режимов — planForecast.ts), нужный темп. Источник правды —
 *  docs/ozon-analytics.md «Экран → План».
 *
 *  Обозначения: D — дней в месяце, e — прошедшие дни месяца ПО ДАННЫМ ПЛОЩАДКИ (`days_elapsed`,
 *  у Ozon и WB разные). Любое деление на 0 и отсутствие плана/факта → null («—» на экране), не 0.
 *
 *  Итоги (площадка = Σ её юрлиц, «Все площадки» = Σ всех юрлиц всех площадок):
 *  - «План» = Σ планов участников с планом, «План на дату» = Σ их plan_to_date (у каждой площадки
 *    свой e: не пересчитывается из суммарного факта);
 *  - «Факт» = Σ по ВСЕМ участникам (абсолютные суммы); «Прогноз» и «Сейчас в день» площадки — по
 *    дневному ряду площадки итогом (`mp.daily`), «Все площадки» — Σ прогнозов площадок; режим
 *    недоступен хотя бы у одной площадки с фактом — итог этого режима недоступен (не Σ части);
 *  - сравнения с планом — только по участникам С планом: % выполнения = Σфакт(с планом)/Σплан*,
 *    отставание = Σфакт(с планом) − Σплан на дату, прогноз к плану = прогноз итога/Σплан* (прогноз
 *    итога — по всем участникам, в т.ч. без плана: ряд площадки целиком),
 *    где Σплан* = Σплан только участников с известным фактом (`plannedPlan`: будущая площадка
 *    смешанного месяца без факта в знаменатель не попадает; столбец «План» — полная Σплан),
 *    «нужно в день» = Σ по участникам с планом.
 *  Участник с фактом без плана входит в факт и прогноз, но не в сравнение: итог помечается
 *  «часть факта без плана» (`unplannedFact`).
 *
 *  Факт null при прошедших днях (e > 0) — «нет данных» (`factMissing`; маржа за месяц, не
 *  покрытый себестоимостью): из факта не считается ничего (отставание, прогноз, темп — null), а не
 *  0; в итогах такой участник не входит ни в факт, ни в сравнение — его план на дату в отставание
 *  не идёт (`plannedPlanToDate`), итог помечается «часть факта — нет данных» (`missingWho`). */
import type { MetricValues, PlanDaily, PlanFactMpOk } from "@/api/mpPlans";
import { forecastsOf, sumForecasts, type DailyValues, type ForecastMode, type Forecasts } from "./planForecast";

/** Состояние месяца по площадке: закрыт (e = D), текущий (0 < e < D), будущий (e = 0). */
export type MonthState = "closed" | "current" | "future";

export function monthStateOf(e: number, D: number): MonthState {
  if (e >= D) return "closed";
  if (e <= 0) return "future";
  return "current";
}

/** Режим таблицы по площадкам без ошибки: есть текущая — полный набор столбцов; иначе есть
 *  закрытая — набор закрытого месяца; иначе — только план. */
export function tableStateOf(states: MonthState[]): MonthState {
  if (states.includes("current")) return "current";
  if (states.includes("closed")) return "closed";
  return "future";
}

export interface PlanCell {
  plan: number | null;
  fact: number | null;
  /** план · e / D */
  planToDate: number | null;
  /** план на дату, который участвует в сравнении: у строки с планом И фактом (знаменатель
   *  отставания в итогах; столбец «План на дату» — полная сумма) */
  plannedPlanToDate: number | null;
  /** факта нет, хотя дни прошли (e > 0): «нет данных», а не будущий месяц */
  factMissing: boolean;
  /** подписи участников с «нет данных» (только у итогов) */
  missingWho: string[];
  /** факт − план на дату */
  lag: number | null;
  /** отставание / план на дату */
  lagPct: number | null;
  /** факт / план */
  donePct: number | null;
  /** прогноз выбранного режима (planForecast.ts); null — недоступен (`forecastReason`) */
  forecast: number | null;
  forecastReason: string | null;
  /** прогнозы всех режимов (подсказка у прогноза) */
  forecasts: Forecasts;
  /** прогноз / план */
  forecastPct: number | null;
  /** «Сейчас в день» — текущий дневной темп выбранного режима (`Forecast.perDay`: avg — факт / e,
   *  last7 — среднее 7 дней, trend — прямая на следующий день) */
  curPerDay: number | null;
  /** (план − факт) / (D − e) */
  needPerDay: number | null;
  /** факт − план (закрытый месяц) */
  deviation: number | null;
  /** план, который участвует в сравнении: у строки с планом И фактом = plan, иначе null (знаменатель
   *  % выполнения, прогноза к плану и отклонения в итогах; Σплан в столбце «План» — полная сумма) */
  plannedPlan: number | null;
  /** факт, который участвует в сравнении с планом (у строки с планом = fact, иначе null) */
  plannedFact: number | null;
  /** факт участников без плана (только у итогов; 0 — нет такого) */
  unplannedFact: number;
  /** подписи участников с фактом без плана (только у итогов) */
  unplannedWho: string[];
}

function div(a: number | null, b: number | null): number | null {
  if (a === null || b === null || b === 0) return null;
  return a / b;
}

function sub(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a - b;
}

/** Ячейка строки юрлица: план и факт показателя, e и D площадки, прогнозы всех режимов и выбранный. */
export function calcCell(
  plan: number | null,
  fact: number | null,
  e: number,
  D: number,
  mode: ForecastMode,
  forecasts: Forecasts,
): PlanCell {
  const planToDate = plan === null || D <= 0 ? null : (plan * e) / D;
  const lag = sub(fact, planToDate);
  const forecast = forecasts[mode].value;
  const left = D - e;
  return {
    plan,
    fact,
    planToDate,
    plannedPlanToDate: plan === null || fact === null ? null : planToDate,
    factMissing: fact === null && e > 0,
    missingWho: [],
    lag,
    lagPct: div(lag, planToDate === null ? null : Math.abs(planToDate)),
    donePct: plan !== null && plan <= 0 ? null : div(fact, plan),
    plannedPlan: plan === null || fact === null ? null : plan,
    plannedFact: plan === null ? null : fact,
    forecast,
    forecastReason: forecasts[mode].reason,
    forecasts,
    forecastPct: div(forecast, plan),
    curPerDay: forecasts[mode].perDay,
    needPerDay: plan !== null && fact !== null && left > 0 ? (plan - fact) / left : null,
    deviation: sub(fact, plan),
    unplannedFact: 0,
    unplannedWho: [],
  };
}

/** Σ значений, где они есть; нет ни одного — null. */
function sumOf(cells: PlanCell[], key: keyof PlanCell): number | null {
  let s: number | null = null;
  for (const c of cells) {
    const v = c[key];
    if (typeof v === "number") s = (s ?? 0) + v;
  }
  return s;
}

/** Итог по участникам (у каждого — свой e): суммы, а не пересчёт из суммарного факта. `who` —
 *  подписи участников по порядку (для пометки «часть факта без плана» и причин прогноза).
 *  `own` — прогнозы итога по его собственному дневному ряду (площадка); нет — Σ прогнозов
 *  участников с фактом (`sumForecasts`: недоступен у одного — недоступен итог). */
export function sumCells(cells: PlanCell[], who: string[], mode: ForecastMode, own?: Forecasts): PlanCell {
  const plan = sumOf(cells, "plan");
  const fact = sumOf(cells, "fact");
  const planToDate = sumOf(cells, "planToDate");
  const plannedPlanToDate = sumOf(cells, "plannedPlanToDate");
  const forecasts =
    own ??
    sumForecasts(
      cells.flatMap((c, i) => (c.fact === null ? [] : [{ who: who[i] ?? "?", fc: c.forecasts }])),
    );
  const forecast = forecasts[mode].value;
  const plannedPlan = sumOf(cells, "plannedPlan");
  const plannedFact = sumOf(cells, "plannedFact");
  const lag = sub(plannedFact, plannedPlanToDate);
  const missingWho: string[] = [];
  cells.forEach((c, i) => {
    if (c.missingWho.length) missingWho.push(...c.missingWho);
    else if (c.factMissing) missingWho.push(who[i] ?? "?");
  });
  let unplannedFact = 0;
  const unplannedWho: string[] = [];
  cells.forEach((c, i) => {
    const own = c.plan === null && c.fact !== null && c.fact !== 0 ? c.fact : 0;
    const part = own + c.unplannedFact;
    if (part !== 0) {
      unplannedFact += part;
      if (c.unplannedWho.length) unplannedWho.push(...c.unplannedWho);
      else unplannedWho.push(who[i] ?? "?");
    }
  });
  return {
    plan,
    fact,
    planToDate,
    plannedPlanToDate,
    factMissing: fact === null && missingWho.length > 0,
    missingWho,
    lag,
    lagPct: div(lag, plannedPlanToDate === null ? null : Math.abs(plannedPlanToDate)),
    donePct: plannedPlan !== null && plannedPlan <= 0 ? null : div(plannedFact, plannedPlan),
    plannedPlan,
    plannedFact,
    forecast,
    forecastReason: forecasts[mode].reason,
    forecasts,
    // прогноз итога (по ряду площадки / Σ площадок) к Σплан*: недоступность прогноза одного
    // юрлица итог не обнуляет
    forecastPct: div(forecast, plannedPlan),
    curPerDay: forecasts[mode].perDay,
    needPerDay: sumOf(cells, "needPerDay"),
    deviation: sub(plannedFact, plannedPlan),
    // пометка нужна, только когда план в итоге есть: без плана все столбцы к плану — «—»
    unplannedFact: plan === null ? 0 : unplannedFact,
    unplannedWho: plan === null ? [] : unplannedWho,
  };
}

/** Подпись юрлица: null — «Без юрлица». */
export function legalLabel(legal: string | null): string {
  return legal ?? "Без юрлица";
}

export interface LegalPlanRow {
  legal: string | null;
  label: string;
  cell: PlanCell;
  /** пометка «у юрлица данных меньше, чем у площадки»; null — нет */
  dataLag: string | null;
}

/** Пометка строки юрлица: у площадки есть прошедшие дни (e > 0), а данные юрлица кончаются раньше
 *  min(данные площадки, конец месяца) или их нет в витрине — отставание идёт по дням площадки (прогноз — от
 *  данных юрлица по его дату, `daily`), у юрлица
 *  фактов меньше дней. Закрытый месяц, полностью покрытый данными юрлица, и будущий месяц (e = 0) —
 *  без пометки. Поля нет (`undefined`) — пометки нет; расчёты пометка не меняет. */
export function legalDataLag(
  rowDataTo: string | null | undefined,
  mpDataTo: string,
  e: number,
  monthEnd: string,
): string | null {
  if (rowDataTo === undefined || e <= 0) return null;
  if (rowDataTo === null) return "нет данных юрлица в витрине";
  const need = mpDataTo < monthEnd ? mpDataTo : monthEnd;
  if (rowDataTo < need) return `данные юрлица по ${rowDataTo.slice(8, 10)}.${rowDataTo.slice(5, 7)}`;
  return null;
}

export interface MpPlanBlock {
  mp: PlanFactMpOk;
  state: MonthState;
  rows: LegalPlanRow[];
  total: PlanCell;
}

function value(v: MetricValues, metric: string): number | null {
  const x = v[metric];
  return typeof x === "number" ? x : null;
}

/** Дневной ряд показателя; ряда нет (месяц не текущий) — null. Показателя нет в ряду при ряде —
 *  ошибка контракта (громко, не «нет ряда»). */
export function dailyOf(daily: PlanDaily | null, metric: string): DailyValues | null {
  if (daily === null) return null;
  const v = daily.values[metric];
  if (v === undefined) throw new Error(`plan-fact: в дневном ряду нет показателя «${metric}»`);
  return v;
}

/** Прогнозы участника: с дневным рядом — от факта по дату ряда (`daily.fact`, `daily.elapsed`:
 *  юрлицо с отстающими данными считается от своей даты), без ряда — от факта и e площадки. */
function forecastsFor(daily: PlanDaily | null, metric: string, fact: number | null, e: number, D: number): Forecasts {
  if (daily === null) return forecastsOf(fact, e, D, null);
  const f = daily.fact[metric];
  if (f === undefined) throw new Error(`plan-fact: в факте дневного ряда нет показателя «${metric}»`);
  return forecastsOf(fact === null ? null : f, daily.elapsed, D, dailyOf(daily, metric));
}

/** Блок площадки по показателю: строки юрлиц и итог площадки (прогноз итога — по ряду площадки). */
export function mpBlock(mp: PlanFactMpOk, metric: string, D: number, month: string, mode: ForecastMode): MpPlanBlock {
  const e = mp.days_elapsed;
  const monthEnd = `${month}-${String(D).padStart(2, "0")}`;
  const rows = mp.rows.map((r) => {
    const fact = value(r.fact, metric);
    return {
      legal: r.legal_name,
      label: legalLabel(r.legal_name),
      cell: calcCell(value(r.plan, metric), fact, e, D, mode, forecastsFor(r.daily, metric, fact, e, D)),
      dataLag: legalDataLag(r.data_to, mp.data_to, e, monthEnd),
    };
  });
  const cells = rows.map((r) => r.cell);
  return {
    mp,
    state: monthStateOf(e, D),
    rows,
    total: sumCells(
      cells,
      rows.map((r) => `${mp.label} · ${r.label}`),
      mode,
      forecastsFor(mp.daily, metric, sumOf(cells, "fact"), e, D),
    ),
  };
}

/** «Все площадки»: Σ юрлиц всех площадок без ошибки (через итоги площадок — суммы те же). */
export function allTotal(blocks: MpPlanBlock[], mode: ForecastMode): PlanCell {
  return sumCells(
    blocks.map((b) => b.total),
    blocks.map((b) => b.mp.label),
    mode,
  );
}

/** Цвет прогноза к плану: ≥ 100% — зелёный, ≥ 90% — жёлтый, ниже — красный. План ≤ 0 (маржа
 *  может планироваться в минус) — без цвета: доля к отрицательному плану не читается как «лучше». */
export function forecastTone(c: PlanCell): "good" | "warn" | "bad" | null {
  if (c.forecastPct === null || c.plan === null || c.plan <= 0) return null;
  if (c.forecastPct >= 1) return "good";
  if (c.forecastPct >= 0.9) return "warn";
  return "bad";
}

/** Текущий месяц YYYY-MM из YYYY-MM-DD. */
export function monthOfDay(day: string): string {
  return day.slice(0, 7);
}

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Месяц из адреса: YYYY-MM; нет или не месяц — текущий (по `today`). */
export function parseMonthParam(v: string | null, today: string): string {
  return v !== null && MONTH_RE.test(v) ? v : monthOfDay(today);
}

/** Сдвиг месяца YYYY-MM на n (± через границу года). */
export function shiftMonth(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + n;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return `${String(yy).padStart(4, "0")}-${String(mm + 1).padStart(2, "0")}`;
}

/** Ввод плана → число или null (пусто — удалить). Пробелы (в т.ч. неразрывные) — разряды,
 *  запятая — десятичная, «−» — минус. Не число — undefined (ошибка ввода, запроса нет). */
export function parsePlanInput(s: string): number | null | undefined {
  const t = s.replace(/[\s\u00a0\u202f]/g, "").replace(/\u2212/g, "-").replace(",", ".");
  if (t === "") return null;
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(t)) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
}

/** Значение плана для поля ввода: «1 234 567» / «1 234 567,5» (обычные пробелы — удобно править). */
export function planInputText(v: number | null): string {
  if (v === null) return "";
  const neg = v < 0;
  const [i, f] = Math.abs(v).toFixed(2).split(".");
  const int = i!.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const frac = f === "00" ? "" : `,${f!.replace(/0$/, "")}`;
  return `${neg ? "-" : ""}${int}${frac}`;
}

/** Юрлицо показывается в редакторе по умолчанию: данные витрины не старше 60 дней до начала
 *  месяца (`date_to` ≥ начало − 60 дн) или план в этом месяце уже есть. */
export function isRecentLegal(dateTo: string | null, month: string): boolean {
  if (!dateTo) return false;
  const start = new Date(`${month}-01T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - 60);
  return dateTo >= start.toISOString().slice(0, 10);
}
